import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, FileDown, ExternalLink, X, PenLine, Calendar, User, Car, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const fmtTr = (v) => {
  if (!v) return "—";
  const s = String(v).slice(0, 19).replace("T", " ");
  return s;
};
const fmtMoney = (n) =>
  new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

/**
 * SignaturePreviewModal — bildirim rozetine tıklanınca açılan imza önizleme.
 *
 * Props:
 *   open           boolean
 *   onClose        () => void
 *   kind           "records" | "receipts" | "intakes" | "deliveries"
 *   recordId       string
 */
export default function SignaturePreviewModal({ open, onClose, kind, recordId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!open || !kind || !recordId) return;
    setLoading(true);
    setData(null);
    (async () => {
      try {
        const { data } = await api.get(`/service/signature/${kind}/${recordId}`);
        setData(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "İmza bilgisi yüklenemedi");
        onClose && onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [open, kind, recordId]);

  if (!open) return null;

  const downloadPdf = async () => {
    if (!data?.pdf_path) return;
    setDownloading(true);
    try {
      const res = await api.get(data.pdf_path.replace("/api", ""), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const name = (data.primary_no || data.plate || data.id || "belge").toString().replace(/[^a-zA-Z0-9_-]/g, "_");
      a.href = url;
      a.download = `${name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF indirilemedi");
    } finally {
      setDownloading(false);
    }
  };

  const openRecord = () => {
    onClose && onClose();
    if (kind === "receipts") nav("/service-receipts");
    else if (kind === "records") nav("/service-records?tab=records");
    else if (kind === "intakes") nav("/service-records?tab=intakes");
    else if (kind === "deliveries") nav("/service-records?tab=deliveries");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="sig-preview-modal"
    >
      <div
        className="bg-background rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-border flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
              <PenLine className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <div className="font-black text-base" data-testid="sig-preview-title">
                İmza Önizleme
              </div>
              <div className="text-xs text-muted-foreground">{data?.label || "Belge"}</div>
            </div>
          </div>
          <button
            className="w-9 h-9 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
            onClick={onClose}
            data-testid="sig-preview-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-7 h-7 animate-spin text-muted-foreground" />
            </div>
          ) : !data ? null : (
            <>
              {/* Meta grid */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {data.primary_no && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1">
                      <Hash className="w-3 h-3" /> {kind === "receipts" ? "Dekont No" : "İş Emri"}
                    </div>
                    <div className="font-mono font-bold" data-testid="sig-preview-no">{data.primary_no}</div>
                  </div>
                )}
                {data.plate && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1">
                      <Car className="w-3 h-3" /> Plaka
                    </div>
                    <div className="font-mono font-bold">{data.plate}</div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1">
                    <User className="w-3 h-3" /> Müşteri
                  </div>
                  <div className="font-bold truncate">{data.customer_name || "—"}</div>
                </div>
                {data.total != null && data.total > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">Toplam</div>
                    <div className="font-mono font-bold">{fmtMoney(data.total)} ₺</div>
                  </div>
                )}
              </div>

              {/* Signature */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1 flex items-center gap-1">
                  <PenLine className="w-3 h-3" /> Müşteri İmzası
                </div>
                <div
                  className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex items-center justify-center min-h-[140px]"
                  data-testid="sig-preview-image-box"
                >
                  {data.signature ? (
                    <img
                      src={data.signature}
                      alt="imza"
                      className="max-h-40 object-contain"
                      data-testid="sig-preview-image"
                    />
                  ) : (
                    <div className="text-xs text-muted-foreground">İmza görseli bulunamadı</div>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" />
                  <span>
                    {data.signed_by_name || "Müşteri"} tarafından imzalandı · {fmtTr(data.signed_at)}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-border flex items-center justify-end gap-2">
          <Button variant="outline" onClick={openRecord} data-testid="sig-preview-goto-btn">
            <ExternalLink className="w-4 h-4 mr-1.5" /> Belgeye Git
          </Button>
          <Button
            onClick={downloadPdf}
            disabled={!data?.pdf_path || downloading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="sig-preview-pdf-btn"
          >
            {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />}
            PDF İndir
          </Button>
        </div>
      </div>
    </div>
  );
}
